import React, { useState, useEffect, useContext, useCallback } from "react";
import gql from "graphql-tag";
import { useHistory, Route, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery, useMutation, useApolloClient } from "react-apollo";
import moment from "moment";
import { endOfDay, format } from "date-fns";
import { Title, Field, Control, Button, Icon, Tag, Label, Dropdown } from "rbx";
import { DeleteButton } from "../../../components/inputs";
import { useCurrentUser, useCompanyParameters } from "../../../utils/hooks";
import DataTable from "../../../components/DataTable";
import { ITEM_TRANSACTION_COLUMNS } from "./columns";
import {
  formatCurrency,
  unmaskCurrency,
  getUTCFormattedDate,
  ourToast as toast
} from "../../../utils";
import Loading from "../../../components/Loading";

import {
  SINGLE_ITEM_TRANSACTION_QUERY,
  CREATE_ITEM_TRANSACTION_MUTATION,
  UPDATE_ITEM_TRANSACTION_MUTATION,
  DELETE_ITEM_TRANSACTION_MUTATION,
  ALL_ITEM_TRANSACTIONS_VIEW_QUERY
} from "../../../graphql/Items/ItemTransactions.graphql";
import { ALL_INVENTORY_ON_HAND_QUERY } from "../../../graphql/Items/InventoryOnHand.graphql";
import { ALL_COMPANY_POSTING_PERIODS_QUERY } from "../../../graphql/CompanyPostingPeriods/CompanyPostingPeriods.graphql";

import { ItemTransactionForm } from "../../../components/ItemTransaction";

import { ModalContext } from "../../../components/ModalProvider";
import { Confirmation } from "../../../components/modals";
import PrintBarcodeModal from "../../../components/PrintBarcodeModal";

const ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

const INITIAL_STATE = {
  RecId: null,
  ItemDescriptionRecIdRef: null,
  ItemNumberRecIdRef: null,
  TransDt: new Date().getTime(),
  VendorNumberRecIdRef: null,
  VendorNameRecIdRef: null,
  TransTypeRecIdRef: "",
  Posted: false,
  PONo: null,
  Quantity: "",
  Cost: "",
  HO: null,
  DimOHData: null,
  LocRecIdRef: "",
  TransNote: "",
  CanEditType: true
};

const ItemTransactionPage = () => {
  const SystemCodes = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        CodeId_not: "2",
        CodeName: "InvMovementType"
      }
    }
  });
  const { id, BatchNo } = useParams();
  const client = useApolloClient();
  const { setModalOpen } = useContext(ModalContext);
  const { data: user } = useCurrentUser();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quantityLoading, setQuantityLoading] = useState(false);
  const [inputs, setInputs] = useState({ ...INITIAL_STATE });
  const history = useHistory();
  const { data: params } = useCompanyParameters();
  const [Codes, setCodes] = useState(null);
  const [SelectedCost, setSelectedCost] = useState(null);

  const getQuantityOnHand = useCallback(
    async updates => {
      try {
        setQuantityLoading(true);
        const {
          InvDim1RecIdRef,
          InvDim2RecIdRef,
          InvDim3RecIdRef,
          ItemNumberRecIdRef,
          LocRecIdRef
        } = updates;
        if (ItemNumberRecIdRef) {
          setQuantityLoading(true);
          const where = {
            ItemRecIdRef: parseInt(ItemNumberRecIdRef.value),
            CompanyLocRecIdRef: parseInt(LocRecIdRef),
            InvDim1RecIdRef: parseInt(InvDim1RecIdRef?.value, 10)
              ? parseInt(InvDim1RecIdRef.value)
              : null,
            InvDim2RecIdRef: parseInt(InvDim2RecIdRef?.value, 10)
              ? parseInt(InvDim2RecIdRef.value)
              : null,
            InvDim3RecIdRef: parseInt(InvDim3RecIdRef?.value, 10)
              ? parseInt(InvDim3RecIdRef.value)
              : null
          };

          const OHData = await client.query({
            query: ALL_INVENTORY_ON_HAND_QUERY,
            variables: {
              where: { ItemRecIdRef: parseInt(ItemNumberRecIdRef.value) }
            },
            fetchPolicy: "network-only"
          });
          const DimOHData = await client.query({
            query: ALL_INVENTORY_ON_HAND_QUERY,
            variables: {
              where
            },
            fetchPolicy: "network-only"
          });
          setInputs({
            ...updates,
            OH:
              (OHData &&
                OHData.data &&
                OHData.data.InventoryOnHands &&
                Array.isArray(OHData.data.InventoryOnHands) &&
                OHData.data.InventoryOnHands.length > 0 &&
                OHData.data.InventoryOnHands.reduce(
                  (a, b) => a + (b.QtyOnHand || 0),
                  0
                )) ||
              "0",
            DimOHData:
              (DimOHData &&
                DimOHData.data &&
                DimOHData.data.InventoryOnHands &&
                Array.isArray(DimOHData.data.InventoryOnHands) &&
                DimOHData.data.InventoryOnHands.length > 0 &&
                DimOHData.data.InventoryOnHands.reduce(
                  (a, b) => a + (b.QtyOnHand || 0),
                  0
                )) ||
              "0"
          });
        }
        setQuantityLoading(false);
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
        setQuantityLoading(false);
      }
    },
    [client]
  );

  const getTransactionTypeByBatch = useCallback(async () => {
    try {
      if (BatchNo) {
        setLoading(true);
        const itemTransactionByBatch = await client.query({
          query: ALL_ITEM_TRANSACTIONS_VIEW_QUERY,
          variables: {
            where: { BatchNo: parseInt(BatchNo) },
            first: 1
          },
          fetchPolicy: "network-only"
        });
        if (
          itemTransactionByBatch.data.VW_ItemTransactions &&
          !itemTransactionByBatch.loading &&
          Array.isArray(itemTransactionByBatch.data.VW_ItemTransactions) &&
          itemTransactionByBatch.data.VW_ItemTransactions.length === 1
        ) {
          setLoading(false);

          const transaction =
            itemTransactionByBatch.data.VW_ItemTransactions[0];

          const updates = {
            TransTypeRecIdRef: transaction.TransTypeRecIdRef,
            CanEditType: !BatchNo
          };
          if (transaction.PONo) {
            updates.PONo = transaction.PONo;
          }
          if (transaction.VendorRecIdRef) {
            updates.VendorRecIdRef = {
              value: transaction.VendorRecIdRef,
              label: `${transaction.VendorName} - ${transaction.VendorNo}`,
              otherLabel: `${transaction.VendorNo} - ${transaction.VendorName}`
            };
            updates.VendorNameRecIdRef = {
              value: transaction.VendorRecIdRef,
              otherLabel: `${transaction.VendorName} - ${transaction.VendorNo}`,
              label: `${transaction.VendorNo} - ${transaction.VendorName}`
            };
          }
          setInputs(prev => ({
            ...prev,
            ...updates
          }));
        }
      }
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [BatchNo, client]);

  const handleQuantityBlur = e => {
    if (
      parseFloat(inputs.DimOHData) + parseFloat(e.target.value) < 0 &&
      inputs.TransTypeRecIdRef &&
      parseInt(inputs.TransTypeRecIdRef, 10) === Codes?.Adjustments?.RecId
    ) {
      toast.error(
        "The entered quantity will bring the on-hand value negative. Please enter another quantity."
      );
    }
  };

  const onChange = async e => {
    const updates = { ...inputs };
    if (e.value && !e.target) {
      updates[e.name] = {
        ...e,
        label: e.label.replace(`- ${e.otherLabel}`, "")
      };

      if (typeof e.StdCost === "number" || typeof e.AvgCost === "number") {
        if (e.name === "ItemDescriptionRecIdRef") {
          updates.ItemNumberRecIdRef = {
            ...e,
            label: e.otherLabel
          };
        } else if (e.name === "ItemNumberRecIdRef") {
          updates.ItemDescriptionRecIdRef = {
            ...e,
            label: e.otherLabel
          };
        }
        if (
          inputs.TransTypeRecIdRef &&
          parseInt(inputs.TransTypeRecIdRef, 10) === Codes?.POReceipts?.RecId
        ) {
          updates.Cost = "";
          setSelectedCost(
            params.InventoryCostMethod.CodeDescription === "Average Cost"
              ? formatCurrency(e.AvgCost)
              : formatCurrency(e.StdCost)
          );
        } else {
          const Cost =
            params.InventoryCostMethod.CodeDescription === "Average Cost"
              ? formatCurrency(e.AvgCost)
              : formatCurrency(e.StdCost);
          setSelectedCost(Cost);
          updates.Cost = Cost;
        }
      }
      if (e.name === "VendorRecIdRef") {
        updates.VendorNameRecIdRef = {
          ...e,
          label: e.otherLabel
        };
      } else if (e.name === "VendorNameRecIdRef") {
        updates.VendorRecIdRef = {
          ...e,
          label: e.otherLabel
        };
      }
    } else if (e.target.name === "Cost") {
      const unmaskedValue = unmaskCurrency(e.target.value);
      updates[e.target.name] = formatCurrency(unmaskedValue);
    } else if (e.target.name === "TransTypeRecIdRef") {
      updates.Cost =
        parseInt(e.target.value, 10) === Codes?.POReceipts?.RecId &&
        SelectedCost
          ? ""
          : SelectedCost;
      updates[e.target.name] = e.target.value;
    } else {
      updates[e.target.name] =
        e.target.type === "checkbox" ? e.target.checked : e.target.value;
    }
    setInputs(updates);

    if (
      e.name &&
      parseInt(inputs.TransTypeRecIdRef, 10) === Codes?.Adjustments?.RecId &&
      (e.name === "InvDim1RecIdRef" ||
        e.name === "InvDim2RecIdRef" ||
        e.name === "InvDim3RecIdRef" ||
        e.name === "ItemDescriptionRecIdRef" ||
        e.name === "ItemNumberRecIdRef")
    ) {
      console.log("getQuantityOnHand");
      await getQuantityOnHand(updates);
    }
  };

  const ItemTransaction = useQuery(SINGLE_ITEM_TRANSACTION_QUERY, {
    variables: { where: { RecId: parseInt(id) || null } },
    fetchPolicy: "network-only"
  });

  const [updateItemTransaction, updateItemTransactionData] = useMutation(
    UPDATE_ITEM_TRANSACTION_MUTATION,
    {
      refetchQueries: [
        "ALL_ITEM_TRANSACTIONS_QUERY",
        "ALL_ITEM_TRANSACTIONS_VIEW_QUERY",
        "SINGLE_ITEM_TRANSACTION_QUERY"
      ]
    }
  );

  const [createItemTransaction, createItemTransactionData] = useMutation(
    CREATE_ITEM_TRANSACTION_MUTATION,
    {
      refetchQueries: [
        "ALL_ITEM_TRANSACTIONS_QUERY",
        "ALL_ITEM_TRANSACTIONS_VIEW_QUERY",
        "SINGLE_ITEM_TRANSACTION_QUERY"
      ]
    }
  );

  const { data: PostingModulesData } = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        CodeName: "PostingModule",
        Category: "PostingPeriods",
        CodeId: "I"
      }
    }
  });
  const { data: PostingPeriodsData } = useQuery(
    ALL_COMPANY_POSTING_PERIODS_QUERY,
    {
      variables: {
        where: {
          Open: true,
          ModuleRecIdRef: PostingModulesData?.SystemCodes[0]?.RecId
        }
      },
      fetchPolicy: "network-only"
    }
  );

  const OpenInventoryCompanyPostingPeriod =
    PostingPeriodsData?.CompanyPostingPeriods[0];

  useEffect(() => {
    if (
      ItemTransaction?.data?.ItemTransaction &&
      !ItemTransaction.loading &&
      !inputs.RecId &&
      !loading
    ) {
      setLoading(true);
      const {
        RecId,
        Item,
        TransDt,
        LocRecIdRef,
        InvDim1,
        InvDim2,
        InvDim3,
        PONo,
        Quantity,
        Cost,
        Vendor,
        TransNote,
        TransTypeRecIdRef,
        Posted,
        PostedBy,
        PostedDate
      } = ItemTransaction.data.ItemTransaction;
      const updates = {
        RecId,
        PostedBy,
        PostedDate,
        ItemNumberRecIdRef: Item && {
          value: Item.RecId.toString(),
          label: Item.ItemNo
        },
        ItemDescriptionRecIdRef: Item && {
          value: Item.RecId.toString(),
          label: Item.Description
        },
        LocRecIdRef,
        TransDt,
        Posted,
        InvDim1RecIdRef: InvDim1 && {
          value: InvDim1.RecId.toString(),
          label: InvDim1.DimCode
        },
        InvDim2RecIdRef: InvDim2 && {
          value: InvDim2.RecId.toString(),
          label: InvDim2.DimCode
        },
        InvDim3RecIdRef: InvDim3 && {
          value: InvDim3.RecId.toString(),
          label: InvDim3.DimCode
        },
        VendorRecIdRef: Vendor && {
          value: Vendor.RecId,
          label: `${Vendor.Name} - ${Vendor.VendorNo}`,
          otherLabel: `${Vendor.VendorNo} - ${Vendor.Name}`
        },
        VendorNameRecIdRef: Vendor && {
          value: Vendor.RecId,
          otherLabel: `${Vendor.VendorNo} - ${Vendor.Name}`,
          label: `${Vendor.VendorNo} - ${Vendor.Name}`
        },
        PONo: PONo || "",
        Quantity,
        BatchNo,
        CanEditType: !BatchNo,
        TransTypeRecIdRef,
        Cost: formatCurrency(Cost),
        TransNote: TransNote || ""
      };
      setEditing(Posted);
      setSelectedCost(formatCurrency(Cost));
      getQuantityOnHand(updates);
    }
    if (inputs && inputs.RecId && params && params.RecId && !inputs.OH) {
      getQuantityOnHand(inputs);
    }
    if (SystemCodes?.data?.SystemCodes?.length > 0 && !Codes) {
      setCodes({
        Adjustments: SystemCodes.data.SystemCodes.find(c => c.CodeId === "3"),
        POReceipts: SystemCodes.data.SystemCodes.find(c => c.CodeId === "1")
      });
    }
    if (
      BatchNo &&
      parseInt(BatchNo, 10) !== 0 &&
      !ItemTransaction.loading &&
      !inputs.TransTypeRecIdRef
    ) {
      getTransactionTypeByBatch();
    }
  }, [
    BatchNo,
    Codes,
    ItemTransaction,
    SystemCodes,
    SystemCodes.data,
    getQuantityOnHand,
    getTransactionTypeByBatch,
    inputs,
    inputs.RecId,
    loading,
    params,
    quantityLoading
  ]);

  const toggleEditing = () => setEditing(!editing);

  const validateIfCanAdd = async data => {
    const ITData = await client.query({
      query: ALL_ITEM_TRANSACTIONS_VIEW_QUERY,
      variables: {
        where: {
          BatchNo: parseFloat(BatchNo),
          InvDim1RecIdRef:
            data.InvDim1RecIdRef && parseInt(data.InvDim1RecIdRef),
          InvDim2RecIdRef:
            data.InvDim2RecIdRef && parseInt(data.InvDim2RecIdRef),
          InvDim3RecIdRef:
            data.InvDim3RecIdRef && parseInt(data.InvDim3RecIdRef),
          ItemRecIdRef: data.ItemRecIdRef && parseInt(data.ItemRecIdRef)
        },
        first: 1
      },
      fetchPolicy: "network-only"
    });
    if (
      ITData &&
      ITData.data &&
      !ITData.loading &&
      ITData.data.VW_ItemTransactions &&
      Array.isArray(ITData.data.VW_ItemTransactions) &&
      ITData.data.VW_ItemTransactions.length === 1
    ) {
      toast.error(
        "Another transaction in this batch already exists for this item and inventory dimension combination. Please edit existing transaction."
      );
      return false;
    }
    return true;
  };

  const handleSave = async (e, Posted, addNew) => {
    e.preventDefault();
    try {
      if (user) {
        if (
          parseFloat(inputs.DimOHData) + parseFloat(inputs.Quantity) < 0 &&
          inputs.TransTypeRecIdRef &&
          parseInt(inputs.TransTypeRecIdRef, 10) === Codes?.Adjustments?.RecId
        ) {
          setInputs({ ...inputs, Quantity: 0 });
          toast.error(
            "The entered quantity will bring the on-hand value negative.  Please enter another quantity."
          );
        } else if (
          parseInt(inputs.TransTypeRecIdRef, 10) === Codes?.POReceipts?.RecId &&
          !inputs.VendorRecIdRef?.value
        ) {
          toast.error("Vendor is required when entering a PO Receipt.");
        } else {
          setLoading(true);
          let editedOrCreated = false;
          const data = {
            ItemRecIdRef:
              inputs.ItemNumberRecIdRef &&
              parseInt(inputs.ItemNumberRecIdRef.value),
            TransDt: getUTCFormattedDate(inputs.TransDt),
            InvDim1RecIdRef:
              inputs.InvDim1RecIdRef && parseInt(inputs.InvDim1RecIdRef.value),
            InvDim2RecIdRef:
              inputs.InvDim2RecIdRef && parseInt(inputs.InvDim2RecIdRef.value),
            InvDim3RecIdRef:
              inputs.InvDim3RecIdRef && parseInt(inputs.InvDim3RecIdRef.value),
            LocRecIdRef: inputs.LocRecIdRef && parseInt(inputs.LocRecIdRef),
            VendorRecIdRef:
              inputs.VendorRecIdRef && parseInt(inputs.VendorRecIdRef.value),
            TransTypeRecIdRef:
              inputs.TransTypeRecIdRef && parseInt(inputs.TransTypeRecIdRef),
            PONo: inputs.PONo,
            Quantity: parseInt(inputs.Quantity),
            Cost: parseFloat(unmaskCurrency(inputs.Cost)),
            TransNote: inputs.TransNote,
            Posted: inputs.Posted,
            CompanyId: params.CompanyId.toString()
          };
          if (Posted) {
            const openPeriodDate = endOfDay(
              new Date(OpenInventoryCompanyPostingPeriod?.PeriodEndDateTime)
            );
            const transactionDate = new Date(inputs.TransDt);

            if (transactionDate > openPeriodDate) {
              toast.error(
                `Transaction date ${format(
                  transactionDate,
                  "MM/dd/yyyy"
                )} is not in the current Inventory Fiscal Period ending ${format(
                  openPeriodDate,
                  "MM/dd/yyyy"
                )}.  Please see Accounting.`
              );
              return;
            }
            data.PostedDate = new Date();
            data.PostingPeriod = openPeriodDate;
            data.Posted = Posted;
            data.PostedByRecIdRef = parseInt(user.RecId);
          }
          if (BatchNo && parseInt(BatchNo, 10) === 0) {
            data.BatchNo = 0;
          } else if (BatchNo && parseInt(BatchNo, 10) !== 0) {
            data.BatchNo = BatchNo && parseInt(BatchNo);
          }
          let itemTransactionData = {};
          if (Posted && BatchNo && parseInt(BatchNo, 10) !== 0) {
            const ITData = await client.query({
              query: ALL_ITEM_TRANSACTIONS_VIEW_QUERY,
              variables: {
                where: { BatchNo: parseFloat(BatchNo) }
              },
              fetchPolicy: "network-only"
            });
            if (
              ITData.data.VW_ItemTransactions &&
              Array.isArray(ITData.data.VW_ItemTransactions) &&
              ITData.data.VW_ItemTransactions.length &&
              !ITData.loading
            ) {
              if (!inputs.RecId) {
                const canAdd = await validateIfCanAdd(data);
                if (canAdd) {
                  itemTransactionData = await createItemTransaction({
                    variables: {
                      data
                    }
                  });
                  editedOrCreated = true;
                }
              } else {
                editedOrCreated = true;
                itemTransactionData = await updateItemTransaction({
                  variables: {
                    data,
                    where: { RecId: parseInt(inputs.RecId) }
                  }
                });
              }
              await Promise.all(
                ITData.data.VW_ItemTransactions.map(async it => {
                  const RecId = !inputs.RecId
                    ? itemTransactionData.data.createItemTransaction.RecId
                    : itemTransactionData.data.updateItemTransaction.RecId;
                  if (parseInt(RecId, 10) !== parseInt(it.RecId, 10)) {
                    await updateItemTransaction({
                      variables: {
                        data: {
                          Posted,
                          CompanyId: params.CompanyId.toString(),
                          PostedDate: new Date(),
                          PostedByRecIdRef: parseInt(user.RecId)
                        },
                        where: { RecId: parseInt(it.RecId) }
                      }
                    });
                  }
                  return it;
                })
              );
            }
          } else if (!inputs.RecId) {
            if (addNew && BatchNo && parseInt(BatchNo, 10) !== 0) {
              const canAdd = await validateIfCanAdd(data);
              if (canAdd) {
                itemTransactionData = await createItemTransaction({
                  variables: {
                    data
                  }
                });
                editedOrCreated = true;
              }
            } else {
              editedOrCreated = true;
              itemTransactionData = await createItemTransaction({
                variables: {
                  data
                }
              });
            }
          } else {
            editedOrCreated = true;
            itemTransactionData = await updateItemTransaction({
              variables: {
                data,
                where: { RecId: parseInt(inputs.RecId) }
              }
            });
          }
          if (editedOrCreated) {
            if (!inputs.RecId) {
              toast.success("Item transaction created successfully.");
            } else {
              toast.success("Item transaction updated successfully.");
            }
            if (!addNew) {
              setInputs({ ...INITIAL_STATE, TransDt: new Date().getTime() });
              history.push("/inventory/item-transactions");
            } else {
              const clone = Object.assign({}, INITIAL_STATE);
              delete clone.TransTypeRecIdRef;

              setInputs({
                ...clone,
                TransDt: new Date().getTime(),
                TransTypeRecIdRef: inputs.TransTypeRecIdRef,
                VendorRecIdRef: inputs.VendorRecIdRef,
                VendorNameRecIdRef: inputs.VendorNameRecIdRef,
                PONo: inputs.PONo,
                CanEditType: false
              });
              const batch = !inputs.RecId
                ? itemTransactionData.data.createItemTransaction.BatchNo
                : itemTransactionData.data.updateItemTransaction.BatchNo;
              history.push(`/inventory/item-transactions/0/details/${batch}`);
            }
          }
          setLoading(false);
        }
      }
    } catch (err) {
      setLoading(false);
      toast.error(
        `Error ${inputs.RecId ? "updating" : "creating"} Item Transaction.`
      );
    }
  };

  const handlePost = async e => {
    e.preventDefault();
    if (user) {
      if (BatchNo && parseInt(BatchNo, 10) !== 0) {
        setModalOpen(
          true,
          <Confirmation
            message="Are you sure you want to post this batch?"
            onConfirm={() => {
              handleSave(e, true, false);
              setModalOpen(false);
            }}
          />
        );
      } else {
        handleSave(e, true, false);
      }
    }
  };

  const handleDelete = async e => {
    e.preventDefault();
    try {
      const performDelete = async () => {
        setLoading(true);
        setModalOpen(false, "");
        await client.mutate({
          mutation: DELETE_ITEM_TRANSACTION_MUTATION,
          variables: {
            where: {
              RecId: parseInt(inputs.RecId)
            }
          },
          refetchQueries: [
            "ALL_ITEM_TRANSACTIONS_QUERY",
            "SINGLE_ITEM_TRANSACTION_QUERY"
          ]
        });
        setLoading(false);
        toast.success("Item transaction deleted successfully.");
        history.push("/inventory/item-transactions");
      };
      setModalOpen(
        true,
        <Confirmation
          message="Are you sure you want to delete?"
          onConfirm={performDelete}
        />
      );
    } catch (err) {
      toast.error("Error deleting Item Transaction.");
    }
  };

  const deleteRow = obj => {
    const item = obj.data[obj.row.id];
    return (
      <DeleteButton
        confirmModalMessage="Are you sure you want to delete?"
        mutation={DELETE_ITEM_TRANSACTION_MUTATION}
        refetchQueries={[
          "ALL_ITEM_TRANSACTIONS_VIEW_QUERY",
          "ALL_ITEM_TRANSACTIONS_QUERY"
        ]}
        showLabel={false}
        size="small"
        variables={{ where: { RecId: parseInt(item && item.RecId) } }}
        onComplete={() => {
          const items = obj.data.filter(t => t.RecId !== item.RecId);
          if (items.length > 0) {
            setInputs({ ...INITIAL_STATE });
            history.push(
              `/inventory/item-transactions/${items[0].RecId}/details/${items[0]
                .BatchNo || 0}`
            );
          } else {
            history.push("/inventory/item-transactions");
          }
        }}
      />
    );
  };

  const editRow = obj => {
    const item = obj.data[obj.row.id];
    return (
      <Button
        size="small"
        type="button"
        onClick={() => {
          setInputs({ ...INITIAL_STATE });
          history.push(
            `/inventory/item-transactions/${
              item.RecId
            }/details/${item.BatchNo || 0}`
          );
        }}
      >
        <Icon>
          <FontAwesomeIcon icon="edit" />
        </Icon>
      </Button>
    );
  };

  const redirectToFirstTransaction = async e => {
    e.preventDefault();
    const { data } = await client.query({
      query: ALL_ITEM_TRANSACTIONS_VIEW_QUERY,
      variables: {
        where: {
          BatchNo: parseFloat(BatchNo)
        }
      }
    });
    if (data?.VW_ItemTransactions?.length > 0) {
      history.push(
        `/inventory/item-transactions/${data.VW_ItemTransactions[0].RecId}/details/${BatchNo}`
      );
    }
  };

  if (
    loading ||
    (updateItemTransactionData && updateItemTransactionData.loading) ||
    (ItemTransaction && ItemTransaction.loading) ||
    (createItemTransactionData && createItemTransactionData.loading) ||
    SystemCodes?.loading ||
    !Codes
  )
    return <Loading />;
  let canSaveOrEdit =
    !inputs.TransTypeRecIdRef ||
    !inputs.ItemNumberRecIdRef ||
    !inputs.Quantity ||
    parseInt(inputs.Quantity) === 0;
  canSaveOrEdit =
    parseInt(inputs.TransTypeRecIdRef, 10) === 1318
      ? !inputs.Cost
      : canSaveOrEdit;

  const handlePrintBarcode = () => {
    setModalOpen(
      true,
      <PrintBarcodeModal
        Dim1={inputs.InvDim1RecIdRef?.label}
        Dim2={inputs.InvDim2RecIdRef?.label}
        Dim3={inputs.InvDim3RecIdRef?.label}
        ItemNo={inputs.ItemNumberRecIdRef?.label}
        Multiplier={inputs.Quantity}
        onComplete={() => setModalOpen(false, "")}
      />
    );
  };

  return (
    <React.Fragment>
      <header className="page-head">
        <div className="page-head-start">
          <Title color="primary" size={2}>
            Item Transaction
          </Title>
          <Tag
            color={inputs.Posted ? "success" : "danger"}
            style={{ fontSize: 12 }}
          >
            {inputs.Posted ? "Posted" : "Unposted"}
          </Tag>
          {BatchNo && parseInt(BatchNo, 10) !== 0 && (
            <Tag color="info" style={{ fontSize: 12 }}>
              Batch No: {BatchNo}
            </Tag>
          )}
          {inputs.PostedBy && (
            <Tag color="light" style={{ fontSize: 12 }}>
              Posted by: {inputs.PostedBy.FirstName} {inputs.PostedBy.LastName}{" "}
              {inputs.PostedDate &&
                moment(inputs.PostedDate).format("MM/DD/YYYY")}
            </Tag>
          )}
        </div>
        <div className="page-head-end">
          <Field kind="group">
            <Control>
              <Button color="info" size="small" onClick={handlePrintBarcode}>
                <Icon>
                  <FontAwesomeIcon icon="barcode" />
                </Icon>
                <span>Print Barcode</span>
              </Button>
            </Control>
            {inputs.RecId && BatchNo && (
              <Control>
                <Button
                  color="link"
                  disabled={inputs.Posted}
                  size="small"
                  onClick={() => {
                    setEditing(false);
                    setInputs({
                      ...INITIAL_STATE,
                      TransDt: new Date().getTime()
                    });
                    setSelectedCost(null);
                    history.push(
                      `/inventory/item-transactions/0/details/${BatchNo}`
                    );
                  }}
                >
                  <Icon>
                    <FontAwesomeIcon icon="plus" />
                  </Icon>
                  <span>Add</span>
                </Button>
              </Control>
            )}
            {inputs.RecId && (
              <Control>
                <Button
                  color="primary"
                  disabled={inputs.Posted}
                  size="small"
                  onClick={toggleEditing}
                >
                  <Icon>
                    <FontAwesomeIcon icon="edit" />
                  </Icon>
                  <span>Edit</span>
                </Button>
              </Control>
            )}
            {inputs.RecId ? (
              <Control>
                <Button
                  color="danger"
                  disabled={inputs.Posted}
                  size="small"
                  onClick={handleDelete}
                >
                  <Icon>
                    <FontAwesomeIcon icon="trash" />
                  </Icon>
                  <span>Delete</span>
                </Button>
              </Control>
            ) : (
              <React.Fragment>
                {BatchNo && BatchNo !== "0" && (
                  <Control>
                    <Button
                      color="link"
                      size="small"
                      onClick={redirectToFirstTransaction}
                    >
                      <Icon>
                        <FontAwesomeIcon icon="check" />
                      </Icon>
                      <span>Done</span>
                    </Button>
                  </Control>
                )}
                <Control>
                  <Button
                    color="danger"
                    size="small"
                    onClick={() => {
                      setInputs({
                        ...INITIAL_STATE,
                        TransDt: new Date().getTime()
                      });
                      setSelectedCost(null);
                    }}
                  >
                    <Icon>
                      <FontAwesomeIcon icon="trash" />
                    </Icon>
                    <span>Clear</span>
                  </Button>
                </Control>
              </React.Fragment>
            )}
            <Control>
              <Dropdown align="right">
                <Dropdown.Trigger>
                  <Button
                    color="success"
                    disabled={editing || canSaveOrEdit}
                    size="small"
                  >
                    <span>Save</span>
                    <Icon size="small">
                      <FontAwesomeIcon icon="angle-down" />
                    </Icon>
                  </Button>
                </Dropdown.Trigger>
                <Dropdown.Menu>
                  <Dropdown.Content>
                    <Dropdown.Item
                      disabled={editing}
                      onClick={e => handleSave(e, false, false)}
                    >
                      <span>Save</span>
                      <Icon className="addButton">
                        <FontAwesomeIcon icon="save" />
                      </Icon>
                    </Dropdown.Item>
                    <Dropdown.Item
                      disabled={editing}
                      onClick={e => handleSave(e, false, true)}
                    >
                      <span>Save &amp; Add New</span>
                      <Icon className="addButton">
                        <FontAwesomeIcon icon="plus" />
                      </Icon>
                    </Dropdown.Item>
                    <Dropdown.Item disabled={editing} onClick={handlePost}>
                      <span>Save &amp; Post</span>
                      <Icon className="addButton">
                        <FontAwesomeIcon icon="level-up-alt" />
                      </Icon>
                    </Dropdown.Item>
                  </Dropdown.Content>
                </Dropdown.Menu>
              </Dropdown>
            </Control>
          </Field>
        </div>
      </header>
      <Route path="/inventory/item-transactions/:id/details/:BatchNo">
        <ItemTransactionForm
          {...{
            PrimaryStorageDimension: params.PrimaryStorageDimension,
            SecondaryStorageDimension: params.SecondaryStorageDimension,
            TertiaryStorageDimension: params.TertiaryStorageDimension,
            UsePrimaryStorageDimension: params.UsePrimaryStorageDimension,
            UseSecondaryStorageDimension: params.UseSecondaryStorageDimension,
            UseTertiaryStorageDimension: params.UseTertiaryStorageDimension,
            inputs,
            onChange,
            editing,
            quantityLoading,
            handleQuantityBlur
          }}
        />
        {BatchNo && parseInt(BatchNo, 10) !== 0 && params && params.RecId && (
          <div>
            <hr />
            <Label>Transactions</Label>
            <DataTable
              columns={ITEM_TRANSACTION_COLUMNS(
                false,
                params,
                true,
                deleteRow,
                editRow,
                inputs.Posted
              )}
              dataQuery={ALL_ITEM_TRANSACTIONS_VIEW_QUERY}
              initialSortBy={[{ id: "CreatedDateTime", desc: false }]}
              name="VW_ItemTransaction"
              where={{ BatchNo: parseFloat(BatchNo) }}
            />
          </div>
        )}
      </Route>
      {/* <pre>
        <code>{JSON.stringify(inputs, null, 2)}</code>
      </pre> */}
    </React.Fragment>
  );
};

ItemTransactionPage.propTypes = {};

export default ItemTransactionPage;
